import { disbatch } from 'redux-act';
import { entitiesResource } from '@gandi/acl';
import { selectUserCountryCode, selectIsAuthenticatedUser } from '@gandi/authentication';
import { notifyDanger } from '@gandi/notifications';
import { selectCurrentId, selectCurrent, selectPersonalId } from '@gandi/redux-sharing';
import { sendBulkCommerce, sendCommerce } from '@gandi/tracking';
import { GET, POST, PATCH, DELETE } from 'gandi.redux-fetcher';
import { logging } from 'gandi.redux-logger';
import { notifyAddedToCart, notifyOneProductAdded, notifyManyProductsAdded, notifyBundleAdded } from '../components/CartWidget/modules/notifications';
import { selectCountItems } from '../components/CartWidget/modules/selectors';
import * as browserStorage from './browser-storage';
import { createAction } from './helpers';
import { selectCartUUID, selectOneProductById, selectProductsByType, selectPollerIdForProduct, selectCartCurrency, selectRawDiscountsArray, selectCartBundles, selectCartTotal, selectCountTotalProducts, selectProductsByTypeAndProcess } from './selectors';
import { selectCartWidgetVisible } from './widget/selectors';
const loggerNamespace = '@gandi/cart';
const DOMAIN_PRODUCTS = ['domains', 'mailboxv2', 'domain_option'];
export const toggleCart = createAction('Toggle cart and its products');
export const setCartUUID = createAction('Set the UUID of the cart');
export const setCartReselleeId = createAction('Set the Resellee Id of the cart');
export const setCartProductOwner = createAction("Set the cart's product owner");
export const setCartOwnerIsReseller = createAction("Set the cart's product owner as reseller organization");
export const mergeCart = createAction('Shallow merge cart state');
export const resetCart = createAction('Reset the cart');
export const createCartStart = createAction('Create a cart [START]');
export const createCartSuccess = createAction('Create a cart [SUCCESS]');
export const createCartFail = createAction('Create a cart [FAIL]');
export const updateCartStart = createAction('Update a cart [START]');
export const updateCartSuccess = createAction('Update a cart [SUCCESS]');
export const updateCartFail = createAction('Update a cart [FAIL]');
export const getCartStart = createAction('Get the cart [START]');
export const getCartSuccess = createAction('Get the cart [SUCCESS]');
export const getCartFail = createAction('Get the cart [FAIL]');
export const fetchCartOwnerListStart = createAction('Fetch cart owner list [START]');
export const fetchCartOwnerListSuccess = createAction('Fetch cart owner list [SUCCESS]');
export const fetchCartOwnerListFail = createAction('Fetch cart owner list [FAIL]');
export const fetchCartConfigurationsStart = createAction('Fetch cart configurations [START]');
export const fetchCartConfigurationsSuccess = createAction('Fetch cart configurations [SUCCESS]');
export const fetchCartConfigurationsFail = createAction('Fetch cart configurations [FAIL]');
export const setCartStatus = createAction('Set the cart status');
export const deleteCartStart = createAction('Delete a cart [START]');
export const deleteCartSuccess = createAction('Delete a cart [SUCCESS]');
export const deleteCartFail = createAction('Delete a cart [FAIL]');
export const commitCartStart = createAction('Commit the cart [START]');
export const commitCartSuccess = createAction('Commit the cart [SUCCESS]');
export const commitCartFail = createAction('Commit the cart [FAIL]');
export const resumeCartStart = createAction('Resume the cart [START]');
export const resumeCartSuccess = createAction('Resume the cart [SUCCESS]');
export const resumeCartFail = createAction('Resume the cart [FAIL]');
export const suggestUpsellStart = createAction('Suggest Upsell [START]');
export const suggestUpsellSuccess = createAction('Suggest Upsell [SUCCESS]');
export const suggestUpsellFail = createAction('Suggest Upsell [FAIL]');
export const addProductStart = createAction('Add a product [START]');
export const addProductSuccess = createAction('Add a product [SUCCESS]');
export const addProductFail = createAction('Add a product [FAIL]');
export const addProductsStart = createAction('Add many products [START]');
export const addProductsFail = createAction('Add many products [FAIL]');
export const updateProductsStart = createAction('Update many products [START]');
export const updateProductsFail = createAction('Update many products [FAIL]');
export const removeProductsStart = createAction('Remove many products [START]');
export const removeProductsFail = createAction('Remove many products [FAIL]');
export const mergeProduct = createAction('Shallow merge product state');
export const addProductOptimistic = createAction('Add a product [OPTIMISTIC]');
export const removeProductOptimistic = createAction('Remove a product [OPTIMISTIC]');
export const updateProductStart = createAction('Update a product [START]');
export const updateProductSuccess = createAction('Update a product [SUCCESS]');
export const updateProductFail = createAction('Update a product [FAIL]');
export const removeProductStart = createAction('Remove a product [START]');
export const removeProductSuccess = createAction('Remove a product [SUCCESS]');
export const removeProductFail = createAction('Remove a product [FAIL]');
export const addDiscountStart = createAction('Add a discount [START]');
export const addDiscountSuccess = createAction('Add a discount [SUCCESS]');
export const addDiscountFail = createAction('Add a discount [FAIL]');
export const removeDiscountStart = createAction('Remove a discount [START]');
export const removeDiscountSuccess = createAction('Remove a discount [SUCCESS]');
export const removeDiscountFail = createAction('Remove a discount [FAIL]');
export const callDomainCatalogStart = createAction('Call catalog to get the product price [START]');
export const callDomainCatalogSuccess = createAction('Call catalog to get the product price [SUCCESS]');
export const callDomainCatalogFail = createAction('Call catalog to get the product price [FAIL]');
export const callDomainCatalogPending = createAction('Call catalog to get the product price [PENDING]');
export const startPollingDomainCatalog = createAction('Start polling domain catalog for the product price');
export const removeDomainCatalogPoller = createAction('Stop polling domain catalog, remove poller');
export const approveContract = createAction('Approve contracts');
export const unsetProductsWorkingStatus = createAction('Unset products working status');
export const addBundleSuccess = createAction('Add bundle [SUCCESS]');
export const stopPollingDomainCatalog = productId => (dispatch, getState) => {
  const pollerId = selectPollerIdForProduct(getState(), {
    productId
  });
  if (pollerId) {
    clearInterval(pollerId);
  }
  return dispatch(removeDomainCatalogPoller(productId));
};
export const callDomainCatalog = domainProduct => (dispatch, getState) => {
  const logger = logging(dispatch, loggerNamespace);
  const {
    id,
    fqdn,
    process
  } = domainProduct;
  dispatch(callDomainCatalogStart(id));
  const state = getState();
  const currency = selectCartCurrency(state);
  const sharingId = selectCurrentId(state);
  let params = {
    name: fqdn,
    processes: process,
    currency
  };
  if (sharingId) {
    params = {
      ...params,
      sharing_id: sharingId
    };
  }
  return dispatch(GET('/prices/domain/details', {
    params,
    on200: response => {
      const {
        products
      } = response.data;
      if (Array.isArray(products) && products[0].status === 'pending') {
        return dispatch(callDomainCatalogPending(id));
      }
      dispatch(stopPollingDomainCatalog(id));
      if (response.data.status === 'error') {
        return dispatch(callDomainCatalogFail(id));
      }
      const apiResults = Array.isArray(products) ? products[0].prices : {};
      return dispatch(callDomainCatalogSuccess({
        productId: id,
        apiResults
      }));
    },
    not200: response => {
      dispatch(callDomainCatalogFail(id));
      const message = `Unable to get the catalog for ${id} (${fqdn}/${process}).`;
      logger.error({
        fqdn,
        process,
        response
      }, message);
      const error = new Error(message);
      error.response = response;
      error.process = process;
      error.fqdn = fqdn;
      throw error;
    }
  }));
};
export const pollDomainCatalog = (domainProduct, options = {}) => dispatch => {
  const logger = logging(dispatch, loggerNamespace);
  const {
    id,
    fqdn,
    process
  } = domainProduct;
  logger.info(domainProduct, `Start Polling Catalog for ${fqdn} - ${id} (${process})`);
  const pollerOptions = {
    maxFailedAttempts: 5,
    interval: 2000,
    ...options
  };
  let failedAttempts = 0;
  const pollerId = setInterval(() => {
    const maxFailedAttempts = pollerOptions.maxFailedAttempts;
    if (failedAttempts < maxFailedAttempts) {
      dispatch(callDomainCatalog(domainProduct)).catch(() => {
        failedAttempts += 1;
        logger.info({
          fqdn,
          process,
          failedAttempts,
          maxFailedAttempts
        }, `Polling Catalog Attempt Failed: ${failedAttempts}/${maxFailedAttempts}`);
      });
    } else {
      logger.error({
        fqdn,
        process,
        failedAttempts,
        maxFailedAttempts
      }, `Stop Polling Catalog for ${fqdn} - ${id} (${process}), exceeded failed attempts!`);
      dispatch(stopPollingDomainCatalog(id));
    }
  }, 1 * pollerOptions.interval);
  dispatch(startPollingDomainCatalog({
    productId: id,
    pollerId
  }));
};
export const stopPollingAllDomainProductsCatalog = () => (dispatch, getState) => {
  const state = getState();
  const products = selectProductsByType(state, {
    productType: 'domain'
  });
  products.forEach(product => {
    if (selectPollerIdForProduct(state, {
      productId: product.id
    })) {
      dispatch(stopPollingDomainCatalog(product.id));
    }
  });
};
export const getCart = (cartId = null) => (dispatch, getState) => {
  const logger = logging(dispatch, loggerNamespace);
  const cartUUID = cartId || selectCartUUID(getState());
  dispatch(getCartStart());
  if (!cartUUID) {
    return dispatch(getCartFail("Cart doesn't exist"));
  }
  return dispatch(GET(`/cart/carts/${cartUUID}`, {
    on200: response => {
      if (response.data.status === 'active' || response.data.status === 'closed') {
        dispatch(getCartSuccess(response.data));
      } else {
        dispatch(getCartFail(response.data.status));
        const message = 'This cart is no longer active.';
        logger.error({
          cartUUID
        }, message);
        const error = new Error(message);
        error.status = response.data.status;
        error.response = response;
        throw error;
      }
    },
    on404: () => {},
    not200: response => {
      dispatch(getCartFail('error'));
      const message = `Unable to fetch the cart ${String(cartUUID)}.`;
      logger.error({
        res: response
      }, message);
      const error = new Error(message);
      error.response = response;
      throw error;
    }
  }));
};
export const fetchCartOwnerList = (cartId_, search) => (dispatch, getState) => {
  const logger = logging(dispatch, loggerNamespace);
  let cartId = cartId_;
  if (!cartId) {
    cartId = selectCartUUID(getState());
  }
  dispatch(fetchCartOwnerListStart());
  return dispatch(GET(`/cart/carts/${cartId}/owners`, {
    params: {
      '~name': search ? `*${search}*` : undefined
    },
    on200: response => {
      dispatch(fetchCartOwnerListSuccess(response.data));
      return response;
    },
    not200: response => {
      const message = 'Unable to fetch cart owner list';
      logger.error({
        res: response
      }, message);
      const error = new Error(message);
      error.response = response;
      throw error;
    }
  })).catch(err => {
    dispatch(fetchCartOwnerListFail(err.response ? err.response.status : undefined));
    throw err;
  });
};
export const getCartUUIDFromOrderId = orderId => dispatch => dispatch(GET(`/cart/carts?order_id=${orderId}`));
export const getLastCart = () => dispatch => {
  const logger = logging(dispatch, loggerNamespace);
  dispatch(getCartStart());
  return dispatch(GET('/cart/carts', {
    on200: response => {
      if (!Array.isArray(response.data) || response.data.length <= 0) {
        logger.info('No last cart found');
        return dispatch(getCartFail('empty'));
      }
      const cart = response.data[0];
      if (cart.status !== 'active') {
        const message = 'getLastCart returned an inactive cart.';
        logger.error({
          response
        }, message);
        const error = new Error(message);
        error.response = response;
        dispatch(getCartFail('inactive'));
        throw error;
      }
      return dispatch(getCart(cart.uuid));
    },
    on404: () => {},
    not200: response => {
      dispatch(getCartFail('error'));
      const message = 'Unable to fetch the cart.';
      logger.error({
        response
      }, message);
      const error = new Error(message);
      error.response = response;
      throw error;
    }
  }));
};
export const suggestUpsell = (params = {}) => dispatch => dispatch(GET('/suggest/upgest', {
  params
}));
export const createCart = params => async (dispatch, getState) => {
  const {
    currency,
    country,
    owner,
    affiliate,
    referer,
    ah
  } = params;
  const logger = logging(dispatch, loggerNamespace);
  dispatch(createCartStart());
  let ownerId = owner;
  if (!ownerId) {
    const state = getState();
    const organization = selectCurrent(state);
    if (organization && organization.subset) {
      ownerId = selectPersonalId(state);
    } else {
      ownerId = selectCurrentId(state);
      if (ownerId) {
        try {
          const {
            hasPermissions
          } = await dispatch(entitiesResource.getEntityPermissions(ownerId));
          if (!hasPermissions(['cart:write'])) {
            ownerId = selectPersonalId(state);
          }
        } catch (err) {
          logger.error({
            err
          }, 'Error fetching cart permissions');
        }
      }
    }
  }
  const body = {
    ...(currency ? {
      currency
    } : Object.freeze({})),
    ...(country ? {
      country
    } : Object.freeze({})),
    ...(ownerId ? {
      owner_uuid: ownerId
    } : Object.freeze({})),
    ...(affiliate ? {
      affiliate
    } : Object.freeze({})),
    ...(referer && affiliate ? {
      referer
    } : Object.freeze({})),
    ...(ah ? {
      ah
    } : Object.freeze({}))
  };
  if (referer && !affiliate) {
    logger.warn('`referer` has been dropped because`affiliate` is missing.');
  }
  return dispatch(POST('/cart/carts', body, {
    headers: {
      'Content-Type': 'application/json'
    },
    on20X: response => {
      const cartUUID = response.data.id;
      const cartCreationDate = new Date(response.headers.get('date'));
      if (!ownerId) {
        browserStorage.setCartId(cartUUID);
      }
      sendCommerce({
        name: 'cart.creation',
        data: {
          cart_id: cartUUID,
          cart_currency: currency,
          cart_creation_utc: `${Math.floor(cartCreationDate.getTime() / 1000)}`
        }
      });
      dispatch(createCartSuccess(cartUUID));
      if (!affiliate) {
        if (ownerId) {
          dispatch(GET(`/discount/vouchers/SHS50/validate?sharing_id=${ownerId}`, {
            on200: res => {
              if (res.data.valid) {
                dispatch(addDiscount('SHS50'));
              }
            },
            not200: res => Promise.resolve(res)
          }));
        } else {
          dispatch(addDiscount('SHS50'));
        }
      }
    },
    not20X: response => {
      dispatch(createCartFail());
      const message = 'Unable to initialize a cart.';
      logger.error({
        res: response
      }, message);
      const error = new Error(message);
      error.currency = currency;
      error.owner = ownerId;
      error.response = response;
      throw error;
    }
  }));
};
export const updateCart = (cartUUID = null, payload = {}, options = {}) => (dispatch, getState) => {
  const logger = logging(dispatch, loggerNamespace);
  if (!cartUUID) {
    cartUUID = selectCartUUID(getState());
  }
  dispatch(updateCartStart());
  return dispatch(PATCH(`/cart/carts/${cartUUID}`, payload, {
    ...options,
    on202: response => {
      dispatch(updateCartSuccess());
      return response;
    },
    not202: response => {
      dispatch(updateCartFail());
      const message = `Unable to update the cart ${String(cartUUID)}.`;
      logger.error({
        cartUUID,
        payload,
        res: response
      }, message);
      const error = new Error(message);
      error.cartUUID = cartUUID;
      error.payload = payload;
      error.response = response;
      throw error;
    }
  }));
};
export const deleteCart = (cartUUID = null) => (dispatch, getState) => {
  const logger = logging(dispatch, loggerNamespace);
  if (!cartUUID) {
    cartUUID = selectCartUUID(getState());
  }
  dispatch(deleteCartStart());
  dispatch(stopPollingAllDomainProductsCatalog());
  return dispatch(DELETE(`/cart/carts/${cartUUID}`, undefined, {
    on204: response => {
      dispatch(deleteCartSuccess());
      return response;
    },
    not204: response => {
      dispatch(deleteCartFail());
      const message = `Unable to delete the cart ${String(cartUUID)}.`;
      const error = new Error(message);
      error._error = response.data.message;
      error.cartUUID = cartUUID;
      logger.error({
        cartUUID,
        err: error,
        res: response
      }, message);
      throw error;
    }
  }));
};
export const commitCart = (options = {}) => (dispatch, getState) => {
  const logger = logging(dispatch, loggerNamespace);
  const cartId = options.cartId || selectCartUUID(getState());
  const sharingId = options.sharingId || selectCurrentId(getState());
  dispatch(commitCartStart());
  return dispatch(POST(`/cart/carts/${cartId}/commit`, {}, {
    headers: {
      'Content-Type': 'application/json'
    },
    params: {
      sharing_id: sharingId
    },
    on200: response => {
      if (response.data.success === 'false') {
        const error = new Error('Unable to commit the cart.');
        error.cartId = cartId;
        error.response = response;
        throw error;
      }
      dispatch(commitCartSuccess());
    },
    not200: response => {
      const errorMsg = response.status === 401 ? '401 Unauthorized on cart commit' : 'Unable to commit the cart.';
      const error = new Error(errorMsg);
      error.response = response;
      error.sharingId = sharingId;
      error.cartId = cartId;
      throw error;
    }
  })).catch(err => {
    const response = err.response;
    if (response && response.data.errors && Array.isArray(response.data.errors) && response.data.errors.length) {
      err.errors = response.data.errors;
    }
    dispatch(commitCartFail());
    logger.error({
      err
    }, err.message);
    throw err;
  });
};
export const resumeCart = (cartUUID = null) => (dispatch, getState) => {
  if (!cartUUID) {
    cartUUID = selectCartUUID(getState());
  }
  dispatch(resumeCartStart());
  return dispatch(POST(`/cart/carts/${cartUUID}/resume`, {}, {
    headers: {
      'Content-Type': 'application/json'
    },
    on200: response => {
      dispatch(resumeCartSuccess());
      return response;
    },
    not200: response => {
      dispatch(resumeCartFail());
      const logger = logging(dispatch, loggerNamespace);
      const message = `Unable to resume cart ${String(cartUUID)}.`;
      logger.error({
        res: response
      }, message);
      const error = new Error(message);
      error.response = response;
      error.cartUUID = cartUUID;
      throw error;
    }
  }));
};
export const refreshCart = (cartUUID = null) => (dispatch, getState) => {
  if (!cartUUID) {
    cartUUID = selectCartUUID(getState());
  }
  return dispatch(POST(`/cart/carts/${cartUUID}/refresh`, {}, {
    headers: {
      'Content-Type': 'application/json'
    },
    on200: response => response,
    not200: response => {
      const logger = logging(dispatch, loggerNamespace);
      const message = `Unable to refresh cart ${String(cartUUID)}.`;
      logger.error({
        res: response
      }, message);
      const error = new Error(message);
      error.response = response;
      error.cartUUID = cartUUID;
      throw error;
    }
  }));
};
const getXShopCountry = state => {
  const xCountryCode = {};
  if (!selectIsAuthenticatedUser(state)) {
    xCountryCode['X-Shop-Country'] = selectUserCountryCode(state);
  }
  return xCountryCode;
};
function getProductQuantity(product) {
  return !!product && !!product.prices && !!product.prices.length && !!product.prices[0] ? product.prices[0].quantity : 1;
}
export const addProduct = ({
  productData,
  extra = {},
  options = {}
}) => (dispatch, getState) => {
  const cartUUID = selectCartUUID(getState());
  const currency = selectCartCurrency(getState());
  const isProductDomain = DOMAIN_PRODUCTS.includes(productData.product_type);
  const isDomainCreate = productData.product_type === 'domain' && productData.process === 'create';
  const domainCreateInCart = selectProductsByTypeAndProcess(getState(), {
    productType: 'domain',
    process: 'create'
  });
  dispatch(addProductStart());
  return dispatch(POST(`/cart/carts/${cartUUID}/products`, productData, {
    headers: {
      ...getXShopCountry(getState()),
      ...options.headers,
      'Content-Type': 'application/json'
    },
    on201: response => {
      sendCommerce({
        name: 'product.add_to_cart',
        data: {
          cart_id: cartUUID,
          product_id: productData.product_type,
          product: `${productData.product_type} - ${productData.process}`,
          product_variant: `${productData.payload?.duration}${productData.payload?.duration_unit || 'y'}`,
          product_brand: isProductDomain ? 'Produits registres' : 'Produits Gandi',
          product_discount: response.data.is_promotion,
          product_pricetaxincluded: response.data.total?.after_taxes,
          product_pricetaxfree: response.data.total?.before_taxes,
          product_currency: currency,
          product_stock: true,
          product_quantity: getProductQuantity(response.data),
          product_cartcreation: options.isProductCartCreation || false
        }
      });
      dispatch(addProductSuccess({
        productData,
        apiResults: response.data,
        extra
      }));
      if (isDomainCreate) {
        const allDomainCreate = [{
          ...productData,
          id: response.data.id
        }, ...domainCreateInCart];
        if (allDomainCreate.length > 10) {
          const domainCreateToUpdate = allDomainCreate.filter(({
            payload
          }) => payload?.mailbox_optin === true).map(domain => ({
            ...domain,
            payload: {
              ...domain.payload,
              mailbox_optin: false
            }
          }));
          dispatch(updateProducts({
            productsData: domainCreateToUpdate,
            options: {
              headers: {
                'X-Dry-Run': 0
              }
            }
          }));
        }
      }
      const cartWidgetVisible = selectCartWidgetVisible(getState());
      if (!cartWidgetVisible && options.notification) {
        const {
          translator,
          buttons,
          message
        } = options.notification;
        if (message) {
          dispatch(notifyAddedToCart({
            translator,
            message,
            buttons
          }));
        } else {
          dispatch(notifyOneProductAdded({
            translator,
            product: {
              ...productData,
              prices: response.data.prices
            },
            buttons
          }));
        }
      }
      return response;
    },
    not201: response => {
      dispatch(addProductFail());
      const label = extra.label || extra.identifier || '';
      const logger = logging(dispatch, loggerNamespace);
      const message = `Unable to add product ${label} to the cart.`;
      logger.error({
        res: response
      }, message);
      const error = new Error(message);
      error.response = response;
      error.product = {
        productData,
        extra
      };
      throw error;
    }
  }));
};
export const addProducts = ({
  productsData,
  extra = {},
  options = {}
}) => (dispatch, getState) => {
  const logger = logging(dispatch, loggerNamespace);
  const cartUUID = selectCartUUID(getState());
  const currency = selectCartCurrency(getState());
  const isDomainCreate = productData => productData.product_type === 'domain' && productData.process === 'create';
  const domainCreateInCart = selectProductsByTypeAndProcess(getState(), {
    productType: 'domain',
    process: 'create'
  });
  const data = productsData.map((item, index) => ({
    body: {
      ...item
    },
    client_ref: (item.identifier || index).toString(),
    method: 'POST',
    uri: `/carts/${cartUUID}/products`
  }));
  dispatch(addProductsStart());
  return dispatch(POST(`/cart/carts/${cartUUID}/bulk/products`, JSON.stringify(data), {
    headers: {
      ...getXShopCountry(getState()),
      ...options.headers,
      'Content-Type': 'application/json'
    },
    on200: response => {
      if (Array.isArray(response.data)) {
        const errors = response.data.filter(rData => rData.status !== 'ok');
        const successes = response.data.filter(rData => rData.status === 'ok').map(rData => {
          const pBody = data.find(pData => pData.client_ref === rData.client_ref) || {};
          return {
            productData: pBody.body,
            apiResults: rData.body,
            extra: extra[rData.client_ref] || {},
            productId: pBody.client_ref ?? undefined
          };
        });
        if (successes.length) {
          const cartWidgetVisible = selectCartWidgetVisible(getState());
          if (!cartWidgetVisible && options.notification) {
            const {
              translator,
              message,
              buttons
            } = options.notification;
            if (message) {
              dispatch(notifyAddedToCart({
                translator,
                message,
                buttons
              }));
            } else {
              dispatch(notifyManyProductsAdded({
                translator,
                successes,
                buttons
              }));
            }
          }
          disbatch(dispatch, ...successes.map(success => addProductSuccess(success)));
          const allDomainCreate = [...successes.filter(s => isDomainCreate(s.productData)).map(s => ({
            ...s.productData,
            id: s.apiResults.id
          })), ...domainCreateInCart];
          if (allDomainCreate.length >= 10) {
            const domainCreateToUpdate = allDomainCreate.filter(({
              payload
            }) => payload?.mailbox_optin === true).map(domain => ({
              ...domain,
              payload: {
                ...domain.payload,
                mailbox_optin: false
              }
            }));
            dispatch(updateProducts({
              productsData: domainCreateToUpdate,
              options: {
                headers: {
                  'X-Dry-Run': 0
                }
              }
            }));
          }
          const productEvents = successes.map(success => {
            const product = selectOneProductById(getState(), {
              productId: success.productId
            });
            if (!product) {
              return null;
            }
            const isProductDomain = DOMAIN_PRODUCTS.includes(product.productType);
            return {
              name: 'product.add_to_cart',
              data: {
                cart_id: cartUUID,
                product_id: product.productType,
                product: `${product.productType} - ${product.process}`,
                product_variant: `${product.payload.duration}${product.payload.duration_unit || 'y'}`,
                product_brand: isProductDomain ? 'Produits registres' : 'Produits Gandi',
                product_discount: product.isPromotion,
                product_pricetaxincluded: product.total?.after_taxes,
                product_pricetaxfree: product.total?.before_taxes,
                product_currency: currency,
                product_stock: true,
                product_quantity: getProductQuantity(product),
                product_cartcreation: options.isProductCartCreation || false
              }
            };
          }).filter(Boolean);
          sendBulkCommerce(productEvents);
        }
        if (errors.length) {
          dispatch(addProductsFail());
          const message = 'Unable to add some products to the cart.';
          logger.error({
            res: response,
            products: errors
          }, message);
          const error = new Error(message);
          error.response = response;
          error.products = errors;
          throw error;
        }
      } else {
        dispatch(addProductsFail());
        const message = 'Unable to add many products to the cart. Response is not an array';
        logger.error({
          res: response,
          products: productsData
        }, message);
        const error = new Error(message);
        error.response = response;
        error.products = productsData;
        throw error;
      }
      return response;
    },
    not200: response => {
      dispatch(addProductsFail());
      const message = 'Unable to add many products to the cart.';
      logger.error({
        res: response,
        products: productsData
      }, message);
      const error = new Error(message);
      error.response = response;
      error.products = productsData;
      throw error;
    }
  }));
};
export const updateProduct = ({
  productId,
  payload,
  options = {}
}) => (dispatch, getState) => {
  const state = getState();
  const logger = logging(dispatch, loggerNamespace);
  const cartUUID = selectCartUUID(state);
  const currency = selectCartCurrency(state);
  const cartTotal = selectCartTotal(state);
  const cartProductsQuantity = selectCountTotalProducts(state);
  const cartDistinctProducts = selectCountItems(state);
  const product = selectOneProductById(state, {
    productId
  });
  if (!product) {
    return Promise.resolve();
  }
  dispatch(updateProductStart(productId));
  return dispatch(PATCH(`/cart/carts/${cartUUID}/products/${productId}`, {
    payload
  }, {
    headers: {
      ...options.headers,
      'Content-Type': 'application/json'
    },
    on202: response => {
      sendCommerce({
        name: 'cart.update',
        data: {
          cart_id: cartUUID,
          cart_currency: currency,
          cart_turnovertaxincluded: cartTotal.afterTaxes,
          cart_turnovertaxfree: cartTotal.beforeTaxes,
          cart_quantity: cartProductsQuantity,
          cart_nbdistinctproduct: cartDistinctProducts
        }
      });
      dispatch(updateProductSuccess({
        productId: product.id,
        payload,
        apiResults: response.data
      }));
    },
    not202: response => {
      dispatch(updateProductFail(productId));
      const message = `Unable to update the product ${product.id} (${product.label || ''}) in the cart.`;
      logger.error({
        res: response,
        payload,
        product
      }, message);
      const error = new Error(message);
      error.response = response;
      error.payload = payload;
      error.product = product;
      throw error;
    }
  }));
};
export const updateProducts = ({
  productsData,
  options = {}
}) => (dispatch, getState) => {
  const state = getState();
  const logger = logging(dispatch, loggerNamespace);
  const cartUUID = selectCartUUID(state);
  if (!productsData) {
    return Promise.resolve();
  }
  const data = productsData.map(item => ({
    body: {
      payload: item.payload
    },
    client_ref: item.id.toString(),
    method: 'PATCH',
    uri: `/carts/${cartUUID}/products/${item.id}`
  }));
  dispatch(updateProductsStart());
  return dispatch(POST(`/cart/carts/${cartUUID}/bulk/products`, JSON.stringify(data), {
    headers: {
      ...options.headers,
      'Content-Type': 'application/json'
    },
    on200: response => {
      if (Array.isArray(response.data)) {
        const errors = response.data.filter(rData => rData.status !== 'ok');
        const successes = response.data.filter(rData => rData.status === 'ok').map(rData => {
          const pBody = data.filter(pData => pData.client_ref === rData.client_ref)[0] || {};
          return {
            productId: pBody.client_ref,
            payload: pBody.body.payload,
            apiResults: rData
          };
        });
        if (successes.length) {
          disbatch(dispatch, ...successes.map(success => updateProductSuccess(success)));
        }
        if (errors.length) {
          dispatch(updateProductsFail());
          const message = 'Unable to update some products in the cart.';
          logger.error({
            res: response,
            products: errors
          }, message);
          const error = new Error(message);
          error.response = response;
          error.products = errors;
          throw error;
        }
      } else {
        dispatch(updateProductsFail());
        const message = 'Unable to update many products in the cart. Response is not an array';
        logger.error({
          res: response,
          products: productsData
        }, message);
        const error = new Error(message);
        error.response = response;
        error.products = productsData;
        throw error;
      }
      return response;
    },
    not200: response => {
      dispatch(updateProductsFail());
      const message = 'Unable to update many products in the cart.';
      logger.error({
        res: response,
        products: productsData
      }, message);
      const error = new Error(message);
      error.response = response;
      error.products = productsData;
      throw error;
    }
  }));
};
export const removeProduct = (productId, options = {
  optimistic: false
}) => (dispatch, getState) => {
  const state = getState();
  const logger = logging(dispatch, loggerNamespace);
  const cartUUID = selectCartUUID(state);
  const currency = selectCartCurrency(state);
  const product = selectOneProductById(state, {
    productId
  });
  if (!product) {
    return Promise.resolve();
  }
  dispatch(removeProductStart(productId));
  if (selectPollerIdForProduct(state, {
    productId: product.id
  })) {
    dispatch(stopPollingDomainCatalog(product.id));
  }
  return dispatch(DELETE(`/cart/carts/${cartUUID}/products/${productId}`, undefined, {
    headers: options.headers || {},
    on204: () => {
      if (options.optimistic) {
        dispatch(removeProductOptimistic(productId));
      }
      const isProductDomain = DOMAIN_PRODUCTS.includes(product.productType);
      sendCommerce({
        name: 'product.remove_from_cart',
        data: {
          cart_id: cartUUID,
          product_id: product.productType,
          product: `${product.productType} - ${product.process}`,
          product_variant: `${product.payload.duration}${product.payload.duration_unit || 'y'}`,
          product_brand: isProductDomain ? 'Produits registres' : 'Produits Gandi',
          product_discount: product.isPromotion,
          product_pricetaxincluded: product.total?.after_taxes,
          product_pricetaxfree: product.total?.before_taxes,
          product_currency: currency,
          product_stock: true,
          product_quantity: getProductQuantity(product)
        }
      });
      return dispatch(removeProductSuccess(productId));
    },
    not204: response => {
      dispatch(removeProductFail(productId));
      const message = `Unable to remove product from the cart.`;
      const error = new Error(message);
      error.response = response;
      error.product = product;
      error._error = response.data.message;
      logger.error({
        res: response
      }, message);
      throw error;
    }
  }));
};
export const removeProducts = productsData => (dispatch, getState) => {
  const logger = logging(dispatch, loggerNamespace);
  const cartUUID = selectCartUUID(getState());
  const data = productsData.map(item => ({
    body: {},
    client_ref: item.id.toString(),
    method: 'DELETE',
    uri: `/carts/${cartUUID}/products/${item.id}`
  }));
  dispatch(removeProductsStart());
  const productsInfos = productsData.map(item => {
    const product = selectOneProductById(getState(), {
      productId: item.id
    });
    if (!product) {
      return null;
    }
    const isProductDomain = DOMAIN_PRODUCTS.includes(product.productType);
    return {
      name: 'product.remove_from_cart',
      data: {
        cart_id: cartUUID,
        product_id: product.productType,
        product: `${product.productType} - ${product.process}`,
        product_variant: `${product.payload.duration}${product.payload.duration_unit || 'y'}`,
        product_brand: isProductDomain ? 'Produits registres' : 'Produits Gandi',
        product_discount: !!product.discounts?.length,
        product_stock: true
      }
    };
  }).filter(Boolean);
  return dispatch(POST(`/cart/carts/${cartUUID}/bulk/products`, JSON.stringify(data), {
    headers: {
      'Content-Type': 'application/json'
    },
    on200: response => {
      if (Array.isArray(response.data)) {
        const errors = response.data.filter(rData => rData.status !== 'ok');
        const successes = response.data.filter(rData => rData.status === 'ok').map(rData => {
          const pBody = data.find(pData => pData.client_ref === rData.client_ref);
          return {
            productId: pBody?.client_ref
          };
        });
        if (successes.length) {
          const deletedProducts = productsInfos.filter(productInfo => successes.some(succeeded => succeeded.productId === productInfo.data.product_id));
          sendBulkCommerce(deletedProducts);
          disbatch(dispatch, ...successes.map(success => removeProductSuccess(success.productId)));
        }
        if (errors.length) {
          dispatch(removeProductsFail());
          const message = 'Unable to remove some products from the cart.';
          logger.error({
            res: response,
            products: errors
          }, message);
          const error = new Error(message);
          error.response = response;
          error.products = errors;
          throw error;
        }
      } else {
        dispatch(removeProductsFail());
        const message = 'Unable to remove many products from the cart. Response is not an array';
        logger.error({
          res: response,
          products: productsData
        }, message);
        const error = new Error(message);
        error.response = response;
        error.products = productsData;
        throw error;
      }
      return response;
    },
    not200: response => {
      dispatch(removeProductsFail());
      const message = 'Unable to remove many products from the cart.';
      logger.error({
        res: response,
        products: productsData
      }, message);
      const error = new Error(message);
      error.response = response;
      error.products = productsData;
      throw error;
    }
  }));
};
export const addBulkDiscount = discounts => (dispatch, getState) => {
  const cartUUID = selectCartUUID(getState());
  const data = discounts.map((discount, index) => ({
    body: {
      ...discount
    },
    client_ref: (discount.code || index).toString(),
    method: 'POST',
    uri: `/carts/${cartUUID}/discounts`
  }));
  return dispatch(POST(`/cart/carts/${cartUUID}/bulk/discounts`, data, {
    headers: {
      'Content-Type': 'application/json'
    },
    on201: response => response,
    not201: response => {
      const logger = logging(dispatch, loggerNamespace);
      const message = 'Unable to add discounts in bulk';
      logger.error({
        response
      }, message);
      const error = new Error(message);
      error.response = response;
      error._error = response.data && response.data.message;
      throw error;
    }
  }));
};
export const addDiscount = (code, relatedProduct) => (dispatch, getState) => {
  const cartUUID = selectCartUUID(getState());
  dispatch(addDiscountStart());
  const discountData = {};
  discountData.code = code;
  if (relatedProduct) {
    discountData.product_id = relatedProduct;
  }
  return dispatch(POST(`/cart/carts/${cartUUID}/discounts`, discountData, {
    headers: {
      'Content-Type': 'application/json'
    },
    on201: response => {
      dispatch(addDiscountSuccess({
        discountData,
        apiResults: response.data
      }));
    },
    not201: response => {
      dispatch(addDiscountFail());
      const logger = logging(dispatch, loggerNamespace);
      const message = `Unable to add the discount code ${discountData.code} to the cart.`;
      logger.info({
        message: response.data && response.data.message
      }, message);
      const error = new Error(message);
      error.response = response;
      error._error = response.data && response.data.message;
      throw error;
    }
  }));
};
export const removeDiscount = argument => (dispatch, getState) => {
  const logger = logging(dispatch, loggerNamespace);
  const cartUUID = selectCartUUID(getState());
  const discounts = selectRawDiscountsArray(getState());
  if (!discounts || !discounts.length) {
    return Promise.resolve();
  }
  const code = typeof argument === 'string' ? argument : argument.code || undefined;
  const discount = code ? discounts.find(d => d?.code === code) : argument.id && discounts.find(d => d?.id === argument.id);
  if (!discount) {
    return Promise.resolve();
  }
  dispatch(removeDiscountStart());
  return dispatch(DELETE(`/cart/carts/${cartUUID}/discounts/${discount.id}`, undefined, {
    on204: () => {
      dispatch(removeDiscountSuccess(code || discount.id));
    },
    not204: response => {
      dispatch(removeDiscountFail());
      const message = `Unable to remove discount from the cart.`;
      logger.error({
        message: response.data && response.data.message
      }, message);
      const error = new Error(message);
      error.response = response;
      error._error = response.data && response.data.message;
      throw error;
    }
  }));
};
export const getCartOrNotify = (__, cartUUID) => dispatch => dispatch(getCart(cartUUID)).catch(e => {
  let message = __('Unable to fetch your Cart.');
  if (e.status && e.status !== 'active') {
    message = __('This Cart is no longer active.');
  }
  return dispatch(notifyDanger({
    message
  }));
});
export const updateAndGetCart = (cartUUID, payload) => dispatch => dispatch(updateCart(cartUUID, payload)).then(() => dispatch(getCart()));
export const setCartOwner = (options = {}) => (dispatch, getState) => {
  const logger = logging(dispatch, loggerNamespace);
  const state = getState();
  const cartUUID = selectCartUUID(state);
  const sharingId = selectCurrentId(state);
  logger.info({
    cartUUID,
    sharingId
  }, 'Set cart owner');
  if (!sharingId) {
    const message = 'Invalid sharingId ! Cannot set cart owner.';
    logger.error({
      cartUUID,
      sharingId
    }, message);
    const error = new Error(message);
    error.sharingId = sharingId;
    error.cartUUID = cartUUID;
    return Promise.reject(error);
  }
  return dispatch(updateCart(cartUUID, {
    owner_uuid: sharingId
  }, options)).then(response => {
    const toMerge = {};
    toMerge.owner = sharingId;
    const lite = !!options.headers && 'X-Dry-Run' in options.headers;
    if (!lite) {
      toMerge.hasOrga = true;
    }
    dispatch(mergeCart(toMerge));
    return response;
  });
};
export const fetchCartConfigurations = () => (dispatch, getState) => {
  const state = getState();
  const cartUUID = selectCartUUID(state);
  const logger = logging(dispatch, loggerNamespace);
  dispatch(fetchCartConfigurationsStart());
  return dispatch(GET(`/cart/carts/${cartUUID}/configurations`, {
    on200: response => {
      dispatch(fetchCartConfigurationsSuccess(response.data));
      return response;
    },
    on404: () => {},
    not200: response => {
      dispatch(fetchCartConfigurationsFail());
      const message = "Unable to fetch the cart's configurations.";
      logger.error({
        res: response
      }, message);
      const error = new Error(message);
      error.response = response;
      throw error;
    }
  }));
};
export const removeBundle = _id => (dispatch, getState) => {
  const state = getState();
  const cartId = selectCartUUID(state);
  const currency = selectCartCurrency(state);
  const logger = logging(dispatch, loggerNamespace);
  const id = typeof _id === 'number' ? _id : Number.parseInt(_id, 10);
  const bundle = selectCartBundles(getState()).find(({
    id: bundleId
  }) => bundleId === id);
  let productIds = [];
  let bundleProducts = [];
  if (bundle) {
    productIds = bundle.products.map(({
      id: productId
    }) => productId);
    bundleProducts = productIds.map(i => {
      const product = selectOneProductById(state, {
        productId: i
      });
      if (!product) {
        return {
          name: 'product.remove_from_cart',
          data: {
            cart_id: cartId,
            product_currency: currency
          }
        };
      }
      const isProductDomain = DOMAIN_PRODUCTS.includes(product.productType);
      return {
        name: 'product.remove_from_cart',
        data: {
          cart_id: cartId,
          product_id: product.productType,
          product: `${product.productType} - ${product.process}`,
          product_variant: `${product.payload.duration}${product.payload.duration_unit || 'y'}`,
          product_brand: isProductDomain ? 'Produits registres' : 'Produits Gandi',
          product_discount: !!product.discounts?.length,
          product_stock: true,
          product_pricetaxincluded: product.total?.after_taxes,
          product_pricetaxfree: product.total?.before_taxes,
          product_currency: currency,
          product_quantity: getProductQuantity(product)
        }
      };
    });
  }
  return dispatch(DELETE(`/cart/carts/${cartId}/bundles/${id}`, undefined, {
    on204: response => {
      if (productIds.length) {
        disbatch(dispatch, ...productIds.map(productId => removeProductSuccess(productId)));
        sendBulkCommerce(bundleProducts);
      }
      return response;
    },
    not204: response => {
      const message = 'Unable to remove cart bundle.';
      logger.error({
        res: response
      }, message);
      const error = new Error(message);
      error.response = response;
      throw error;
    }
  }));
};
export const addBundle = ({
  products,
  type,
  grids,
  useOnce,
  notification
}) => (dispatch, getState) => {
  const cartId = selectCartUUID(getState());
  const currency = selectCartCurrency(getState());
  return dispatch(POST(`/cart/carts/${cartId}/bundles`, {
    products,
    type,
    grids,
    use_once: !!useOnce
  }, {
    headers: {
      'X-Dry-Run': 0,
      ...getXShopCountry(getState())
    },
    on201: response => {
      dispatch(addBundleSuccess());
      const cartWidgetVisible = selectCartWidgetVisible(getState());
      if (!cartWidgetVisible && notification) {
        const {
          translator,
          message,
          buttons
        } = notification;
        if (message) {
          dispatch(notifyAddedToCart({
            translator,
            message,
            buttons
          }));
        } else {
          dispatch(notifyBundleAdded({
            translator,
            buttons
          }));
        }
      }
      dispatch(getCart());
      const bundleEvents = products.map(product => {
        return {
          name: 'product.add_to_cart',
          data: {
            cart_id: cartId,
            product_id: product.product_type,
            product: `${product.product_type} - ${product.process}`,
            product_variant: `${product.payload.duration}y`,
            product_brand: 'Produits registres',
            product_discount: !!product.discount_code,
            product_stock: true,
            product_currency: currency,
            product_quantity: 1
          }
        };
      });
      sendBulkCommerce(bundleEvents);
      return Promise.resolve(response);
    },
    not201: response => Promise.reject(response)
  }));
};
export const updateProductAndGetCartOrNotify = (__, productId, payload, options) => dispatch => dispatch(updateProduct({
  productId,
  payload,
  options
})).catch(err => dispatch(notifyDanger({
  message: __('Unable to update %(label)s.', {
    label: err.product.label
  })
}))).then(() => dispatch(getCart())).catch(() => dispatch(notifyDanger({
  message: __('Unable to update your Cart. Please refresh the page.')
})));
export const getIsValidAndAnonymousCart = cartId => dispatch => dispatch(GET(`/cart/carts/${cartId}/anonymous`, {
  on401: res => res
})).then(response => response.status === 204).catch(() => false);
const getValidAnonymousCartId = () => async dispatch => {
  if (!browserStorage.hasCartId()) {
    return false;
  }
  const anonymousCartId = browserStorage.getCartId();
  if (!anonymousCartId) {
    return false;
  }
  const isValidAnonymousCart = await dispatch(getIsValidAndAnonymousCart(anonymousCartId)).catch(() => false);
  if (!isValidAnonymousCart) {
    browserStorage.clearCartId();
    return false;
  }
  if (!browserStorage.hasCartIdInCookie()) {
    browserStorage.setCartIdToCookies(anonymousCartId);
  }
  return anonymousCartId;
};
export const cleanupBrowserStorage = () => (dispatch, getState) => {
  const authenticated = selectIsAuthenticatedUser(getState());
  const cartId = selectCartUUID(getState());
  if (authenticated && cartId === browserStorage.getCartId()) {
    browserStorage.clearCartId();
  }
};
const fetchCart = cartId => async (dispatch, getState) => {
  const logger = logging(dispatch, loggerNamespace);
  const authenticated = selectIsAuthenticatedUser(getState());
  try {
    const cart = await dispatch(getCart(cartId));
    if (!cart.hasOrga && authenticated) {
      await dispatch(setCartOwner()).catch(err => {
        logger.error({
          err
        }, 'unable to set owner to cart');
      });
      dispatch(cleanupBrowserStorage());
    }
    return true;
  } catch (err) {
    logger.error({
      err
    }, 'unable to retrieve cart from id.');
    return false;
  }
};
export const retrieveCart = cartId => async (dispatch, getState) => {
  const logger = logging(dispatch, loggerNamespace);
  const authenticated = selectIsAuthenticatedUser(getState());
  if (cartId) {
    logger.info('Retrieve cart from params.');
    const success = await dispatch(fetchCart(cartId));
    if (success) {
      return true;
    }
  }
  const anonymousCartId = await dispatch(getValidAnonymousCartId());
  if (anonymousCartId) {
    logger.info('Fallback to retrieve cart from browser storage.');
    const success = await dispatch(fetchCart(anonymousCartId));
    if (success) {
      return true;
    }
  }
  if (authenticated) {
    try {
      logger.info('Fallback to retrieve last cart.');
      await dispatch(getLastCart());
      return true;
    } catch (err) {
      logger.error({
        err
      }, 'unable to retrieve last cart.');
    }
  }
  return false;
};